import { ArrowRight, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Breadcrumbs from "../components/Breadcrumbs";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";
import { getGlossaryTermBySlug, visibleGlossaryTerms } from "../content/glossary";

type GlossaryTermPageProps = { slug: string };

export default function GlossaryTermPage({ slug }: GlossaryTermPageProps) {
  const entry = getGlossaryTermBySlug(slug);

  if (!entry) {
    return (
      <>
        <PageSEO
          title="Term not found | Anter Glossary"
          description="This glossary term is unavailable."
          canonical="/glossary"
          noindex
        />
        <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
          <SiteHeader scrolled />
          <main
            id="main-content"
            tabIndex={-1}
            className="mx-auto w-full max-w-3xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
          >
            <h1 className="mb-3 text-3xl font-extrabold tracking-tight text-text">
              Term not found
            </h1>
            <p className="mb-6 text-text-muted">This glossary term is unavailable.</p>
            <a
              href="/glossary"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-text"
            >
              Browse the glossary
              <ArrowRight className="h-4 w-4" />
            </a>
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  const canonical = `/glossary/${entry.slug}`;
  const related = entry.related
    .map((relatedSlug) => visibleGlossaryTerms.find((t) => t.slug === relatedSlug))
    .filter((t): t is NonNullable<typeof t> => Boolean(t));

  // `DefinedTerm` inside a `DefinedTermSet` is the correct schema.org shape for
  // a glossary — it tells Google (and LLM crawlers) that this page defines a
  // specific term rather than being general prose that mentions it.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": `https://anter.ai${canonical}`,
    name: entry.term,
    description: entry.shortDefinition,
    url: `https://anter.ai${canonical}`,
    ...(entry.aliases.length > 0 ? { alternateName: entry.aliases } : {}),
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      "@id": "https://anter.ai/glossary",
      name: "Anter AI Agent Glossary",
      url: "https://anter.ai/glossary",
    },
  };

  return (
    <>
      <PageSEO
        title={`${entry.term} — AI Agent Glossary | Anter`}
        description={entry.shortDefinition}
        canonical={canonical}
        jsonLd={jsonLd}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
        <SiteHeader scrolled />

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-3xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Glossary", href: "/glossary" }, { label: entry.term }]} />

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
            {entry.category}
          </p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
            {entry.term}
          </h1>

          {/* The one-sentence definition is lifted out of the body so it reads
              as a direct answer — this is the block most likely to be quoted in
              an AI Overview or featured snippet. */}
          <p className="mb-8 rounded-2xl border border-primary-border/60 bg-primary-dim/40 p-5 text-lg leading-relaxed text-text">
            {entry.shortDefinition}
          </p>

          <article className="article-rich-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children, ...props }) => {
                  const isExternal = typeof href === "string" && href.startsWith("http");
                  return (
                    <a
                      href={href}
                      {...props}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noreferrer" : undefined}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {entry.content}
            </ReactMarkdown>
          </article>

          <section className="mt-12 rounded-2xl border border-border bg-bg-secondary/60 p-6">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-text">
              <BookOpen className="h-4 w-4 text-primary" />
              In the Anter docs
            </h2>
            <ul className="space-y-2">
              {entry.docsRefs.map((ref) => (
                <li key={ref.href}>
                  <a
                    href={ref.href}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-text underline-offset-4 hover:underline"
                  >
                    {ref.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {related.length > 0 && (
            <section className="mt-8">
              <h2 className="mb-4 text-sm font-bold text-text">Related terms</h2>
              <div className="flex flex-wrap gap-2">
                {related.map((t) => (
                  <a
                    key={t.slug}
                    href={`/glossary/${t.slug}`}
                    className="rounded-full border border-border bg-bg-secondary/60 px-3.5 py-1.5 text-xs font-medium text-text transition hover:border-primary-border hover:bg-bg-hover"
                  >
                    {t.term}
                  </a>
                ))}
              </div>
            </section>
          )}

          <p className="mt-10 text-xs text-text-faint">
            Last updated{" "}
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(new Date(entry.updatedAt))}
          </p>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
